import { useTranslations } from 'next-intl';
import { IProduct } from '@monofront/types';
import {
  getBundleProductDetails,
  getIsAntiWaste,
  getIsFallingPriceFromTags,
  getIsFrozenPriceFromTags,
  getIsNew,
  getIsUltraFresh,
  getMalinDiscount,
} from '@monofront/business/utils';
import { useFormatAntiWasteTag } from '@monofront/business/hooks/product/useFormatAntiWasteTag';
import ImageViewer, {
  ImageViewerCorner,
} from '@monofront/design-system/organisms/ImageViewer';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';
import New from '@monofront/design-system/visuals/Pictograms/New';
import UltraFresh from '@monofront/design-system/visuals/Pictograms/UltraFresh';
import BadgeFrozenPrice from '@monofront/design-system/visuals/Icons/BadgeFrozenPrice';
import BadgeFallingPrice from '@monofront/design-system/visuals/Icons/BadgeFallingPrice';
import { useProductCustomer } from '@monofront/business/contexts';

interface IProductHeroProps {
  product: IProduct;
  UNSAFE_className?: string;
}

const ProductHero: React.FC<IProductHeroProps> = ({
  product,
  UNSAFE_className = '',
}) => {
  const { isMalin } = useProductCustomer();
  const tCatalog = useTranslations('Catalog');
  const tProducts = useTranslations('Products');
  const isAntiWaste = getIsAntiWaste(product.tags);
  const isNew = getIsNew(product.tags);
  const isUltraFresh = getIsUltraFresh(product.tags);
  const isFrozenPrice = getIsFrozenPriceFromTags(product.tags);
  const isFallingPrice = getIsFallingPriceFromTags(product.tags);
  const recupTag = useFormatAntiWasteTag(product.tags);
  const { productBundleNumber, productBundleSale } = getBundleProductDetails(
    product.tags,
  );
  const isKit = Boolean(productBundleSale && product.combination_product_data);
  const isBundle = Boolean(!isKit && productBundleNumber && productBundleSale);
  const malinDiscount = getMalinDiscount(product.tags);

  if (!product.images || product.images.length === 0) return null;

  const images = product.images_with_alt?.length
    ? product.images_with_alt.map((image) => ({
        src: image.src,
        alt: image.alt || product.title,
      }))
    : product.images.map((image) => ({
        src: image,
        alt: product.title,
      }));

  return (
    <ImageViewer
      images={images}
      disabledMessage={
        !product?.inventory_available
          ? isAntiWaste
            ? tCatalog('endOfStock')
            : tCatalog('comesBackSoon')
          : ''
      }
      hasZoom
      UNSAFE_className={UNSAFE_className}
    >
      {/* Top Left Corner: New tag */}
      {isNew && (
        <ImageViewerCorner position="top-left">
          <New aria-label={tCatalog('new')} />
        </ImageViewerCorner>
      )}

      {/* Top Right Corner:
        - 1st line => In order of priority: Recup (eg. Antiwaste > DDM) tag > Falling price > Frozen Price
        - 2nd line => Bundle tag
      */}
      {Boolean(
        recupTag ||
          isFallingPrice ||
          isFrozenPrice ||
          isKit ||
          isBundle ||
          malinDiscount,
      ) && (
        <ImageViewerCorner
          position="top-right"
          UNSAFE_className="tw-flex tw-flex-col tw-items-end tw-gap-1"
        >
          {recupTag ? (
            <p className="tw-lf-tag tw-lf-tag-md tw-lf-tag-decorative-danger">
              {recupTag}
            </p>
          ) : isFallingPrice ? (
            <p className="tw-lf-tag tw-lf-tag-md tw-lf-tag-decorative-danger">
              <BadgeFallingPrice />
              {tCatalog('tagFallingPrice')}
            </p>
          ) : isFrozenPrice ? (
            <p className="tw-lf-tag tw-lf-tag-md tw-lf-tag-decorative-danger">
              <BadgeFrozenPrice />
              {tCatalog('tagFrozenPrice')}
            </p>
          ) : null}

          {malinDiscount && isMalin ? (
            <p className="tw-lf-tag tw-lf-tag-md tw-lf-tag-alert">
              {tCatalog('tagMalin')}
            </p>
          ) : isKit ? (
            <p className="tw-lf-tag tw-lf-tag-md tw-lf-tag-alert">
              -{productBundleSale}% {tCatalog('versusUnitPrice')}
            </p>
          ) : isBundle ? (
            <p className="tw-lf-tag tw-lf-tag-md tw-lf-tag-alert">
              -{productBundleSale}%{' '}
              {tCatalog('fromNbProducts', {
                number: productBundleNumber,
              })}
            </p>
          ) : null}
        </ImageViewerCorner>
      )}

      {/* Bottom Left Corner: Ultra Fresh tag */}
      {isUltraFresh && (
        <ImageViewerCorner position="bottom-left">
          <p className="tw-lf-tag tw-lf-tag-md tw-lf-tag-decorative-blue-accent">
            <UltraFresh size={IconSizes.sm} />
            {product.shelf_life_at_reception && (
              <span>
                {tProducts('tagConsumptionLimit', {
                  days: product.shelf_life_at_reception,
                })}
              </span>
            )}
          </p>
        </ImageViewerCorner>
      )}
    </ImageViewer>
  );
};

export default ProductHero;
